import React from "react";
import { cn } from "../../utils/cn";
import Label from "./Label.jsx";
import HelperText from "./HelperText.jsx";

const STEP_ICON = "/icons/appointments/dashboard/unfold_more.svg";

function decimalPlaces(value) {
  const text = String(value ?? "");
  if (!text.includes(".")) return 0;
  return text.split(".")[1]?.length || 0;
}

function normalizeNumber(value, step) {
  if (!Number.isFinite(value)) return "";
  const precision = Math.min(Math.max(decimalPlaces(step), decimalPlaces(value)), 8);
  return Number(value.toFixed(precision)).toString();
}

function clamp(value, min, max) {
  let next = value;
  if (typeof min === "number" && Number.isFinite(min)) next = Math.max(min, next);
  if (typeof max === "number" && Number.isFinite(max)) next = Math.min(max, next);
  return next;
}

const AfriMedNumberField = React.forwardRef(
  (
    {
      id,
      name,
      label,
      value,
      defaultValue = "",
      onChange,
      min,
      max,
      step = 1,
      required = false,
      disabled = false,
      readOnly = false,
      error,
      helperText,
      placeholder,
      size = "default",
      tone = "surface",
      allowEmpty = true,
      className,
      inputClassName,
      incrementLabel = "Increase value",
      decrementLabel = "Decrease value",
      ...props
    },
    ref
  ) => {
    const reactId = React.useId();
    const inputId = id || `afrimed-number-${reactId}`;
    const helperId = helperText || error ? `${inputId}-help` : undefined;
    const controlled = value !== undefined;
    const [innerValue, setInnerValue] = React.useState(defaultValue === undefined ? "" : String(defaultValue));
    const stringValue = controlled ? (value === null || value === undefined ? "" : String(value)) : innerValue;
    const numericValue = stringValue === "" ? null : Number(stringValue);
    const numericStep = Number(step) || 1;
    const numericMin = min === undefined || min === "" ? undefined : Number(min);
    const numericMax = max === undefined || max === "" ? undefined : Number(max);
    const canStep = !disabled && !readOnly;
    const atMin = numericValue !== null && Number.isFinite(numericValue) && typeof numericMin === "number" && numericValue <= numericMin;
    const atMax = numericValue !== null && Number.isFinite(numericValue) && typeof numericMax === "number" && numericValue >= numericMax;
    const compact = size === "small" || size === "compact";

    const setValue = React.useCallback(
      (nextValue, sourceEvent) => {
        if (!controlled) setInnerValue(nextValue);
        onChange?.({
          type: "change",
          target: { id: inputId, name, value: nextValue },
          currentTarget: { id: inputId, name, value: nextValue },
          nativeEvent: sourceEvent?.nativeEvent || sourceEvent,
        });
      },
      [controlled, inputId, name, onChange]
    );

    const stepFrom = React.useCallback(
      (direction, sourceEvent) => {
        if (!canStep) return;
        const base =
          numericValue !== null && Number.isFinite(numericValue)
            ? numericValue
            : typeof numericMin === "number"
              ? numericMin
              : 0;
        const next = clamp(base + direction * numericStep, numericMin, numericMax);
        setValue(normalizeNumber(next, numericStep), sourceEvent);
      },
      [canStep, numericValue, numericMin, numericMax, numericStep, setValue]
    );

    const onInputChange = (event) => {
      const next = event.target.value;
      if (next === "" && !allowEmpty) return;
      setValue(next, event);
    };

    const onInputBlur = (event) => {
      if (stringValue !== "" && Number.isFinite(Number(stringValue))) {
        const next = clamp(Number(stringValue), numericMin, numericMax);
        const formatted = normalizeNumber(next, numericStep);
        if (formatted !== stringValue) setValue(formatted, event);
      }
      props.onBlur?.(event);
    };

    const onInputKeyDown = (event) => {
      if (event.key === "ArrowUp") {
        event.preventDefault();
        stepFrom(1, event);
        return;
      }
      if (event.key === "ArrowDown") {
        event.preventDefault();
        stepFrom(-1, event);
        return;
      }
      props.onKeyDown?.(event);
    };

    return (
      <div className={cn("afrimed-number-field min-w-0", className)}>
        {label ? (
          <Label htmlFor={inputId} error={!!error} className="mb-1">
            {label}
            {required ? <span className="ml-1 text-[rgb(var(--schemes-critical))]">*</span> : null}
          </Label>
        ) : null}

        <div
          className={cn(
            "group flex w-full min-w-0 overflow-hidden rounded-[12px] border text-on-surface transition",
            tone === "ghost"
              ? "bg-[rgb(var(--clr-surface-container-low))]"
              : "bg-[rgb(var(--clr-surface-cont-highest))]",
            compact ? "h-10" : "h-11",
            error
              ? "border-[rgb(var(--schemes-critical))] focus-within:ring-[rgba(var(--schemes-critical),0.16)]"
              : "border-[rgba(var(--clr-outline),0.95)] focus-within:border-[rgba(var(--afrimed-primary),0.48)] focus-within:ring-[rgba(var(--afrimed-primary),0.14)]",
            "focus-within:ring-2",
            disabled ? "opacity-60" : "",
            readOnly ? "bg-[rgba(var(--clr-on-surface),0.035)]" : ""
          )}
        >
          <input
            {...props}
            ref={ref}
            id={inputId}
            name={name}
            type="number"
            inputMode="decimal"
            value={stringValue}
            min={min}
            max={max}
            step={step}
            required={required}
            disabled={disabled}
            readOnly={readOnly}
            placeholder={placeholder}
            aria-invalid={!!error}
            aria-describedby={helperId}
            aria-valuemin={min}
            aria-valuemax={max}
            aria-valuenow={numericValue !== null && Number.isFinite(numericValue) ? numericValue : undefined}
            onChange={onInputChange}
            onBlur={onInputBlur}
            onKeyDown={onInputKeyDown}
            onWheel={(event) => event.currentTarget.blur()}
            className={cn(
              "afrimed-number-field__input min-w-0 flex-1 border-0 bg-transparent px-3 text-body-medium text-on-surface outline-none placeholder:text-on-surface-var/70 disabled:cursor-not-allowed",
              compact ? "h-10" : "h-11",
              inputClassName
            )}
          />
          <div className="flex w-8 shrink-0 flex-col border-l border-[rgba(var(--clr-outline),0.82)]">
            <button
              type="button"
              tabIndex={-1}
              aria-label={incrementLabel}
              disabled={!canStep || atMax}
              onClick={(event) => stepFrom(1, event)}
              className="flex min-h-0 flex-1 items-center justify-center text-on-surface-var transition hover:bg-[rgba(var(--clr-on-surface),0.06)] disabled:cursor-not-allowed disabled:opacity-35"
            >
              <span
                aria-hidden="true"
                className="block h-[9px] w-[18px] bg-current"
                style={{
                  WebkitMask: `url("${STEP_ICON}") center top / 18px 18px no-repeat`,
                  mask: `url("${STEP_ICON}") center top / 18px 18px no-repeat`,
                }}
              />
            </button>
            <button
              type="button"
              tabIndex={-1}
              aria-label={decrementLabel}
              disabled={!canStep || atMin}
              onClick={(event) => stepFrom(-1, event)}
              className="flex min-h-0 flex-1 items-center justify-center border-t border-[rgba(var(--clr-outline),0.55)] text-on-surface-var transition hover:bg-[rgba(var(--clr-on-surface),0.06)] disabled:cursor-not-allowed disabled:opacity-35"
            >
              <span
                aria-hidden="true"
                className="block h-[9px] w-[18px] bg-current"
                style={{
                  WebkitMask: `url("${STEP_ICON}") center bottom / 18px 18px no-repeat`,
                  mask: `url("${STEP_ICON}") center bottom / 18px 18px no-repeat`,
                }}
              />
            </button>
          </div>
        </div>

        {(helperText || error) && (
          <HelperText id={helperId} error={!!error} className="mt-1">
            {error || helperText}
          </HelperText>
        )}

        <style>{`
          .afrimed-number-field__input::-webkit-outer-spin-button,
          .afrimed-number-field__input::-webkit-inner-spin-button {
            -webkit-appearance: none;
            margin: 0;
          }
          .afrimed-number-field__input {
            -moz-appearance: textfield;
            appearance: textfield;
          }
        `}</style>
      </div>
    );
  }
);

AfriMedNumberField.displayName = "AfriMedNumberField";

export default AfriMedNumberField;
